// 開発環境用のモックAPIクライアント
interface MockProduct {
  id: number;
  title: string;
  handle: string;
  variants: Array<{
    id: number;
    title: string;
    price: string;
    inventory_quantity: number;
  }>;
  images: Array<{
    id: number;
    src: string;
    alt: string;
  }>;
  tags: string[];
  product_type: string;
  status: 'active' | 'draft'; // 商品の公開状態
}

interface MockCartItem {
  productId: number;
  variantId: number;
  quantity: number;
  properties: Record<string, string>;
}

class DevShopifyAPI {
  // 商品一覧を取得（プロキシ経由）
  async getProducts(): Promise<MockProduct[]> {
    console.log('🔧 [DEV] Getting products from proxy...');

    try {
      const response = await fetch('/api/shopify/products.json');
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('Failed to fetch products:', error);
      return [];
    }
  }

  // 特定の商品を取得（プロキシ経由）
  async getProduct(productId: number): Promise<MockProduct> {
    console.log(`🔧 [DEV] Getting product ${productId} from proxy...`);

    try {
      const response = await fetch(`/api/shopify/products/${productId}.json`);
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      return data.product;
    } catch (error) {
      console.error('Failed to fetch product:', error);
      throw error;
    }
  }

  // カートに商品を追加（モック - カート機能はShopify側で管理するため、ここは簡易的な実装のままにするか、必要に応じて実装）
  async addToCart(item: MockCartItem): Promise<any> {
    console.log('🔧 [DEV] Adding to cart:', item);

    await new Promise(resolve => setTimeout(resolve, 800));

    return {
      success: true,
      message: 'カートに追加されました（開発環境）',
      cartItem: {
        id: Date.now(),
        ...item,
        addedAt: new Date().toISOString()
      }
    };
  }

  // 画像をアップロード（モック - 実際にはサーバーへのアップロードが必要だが、一旦モックのまま）
  async uploadImage(file: File): Promise<string> {
    console.log('🔧 [DEV] Uploading image:', file.name);

    await new Promise(resolve => setTimeout(resolve, 1000));

    // 実際のファイルアップロードをシミュレート
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(file);
    });
  }

  // 注文を作成（プロキシ経由）
  async createOrder(orderData: {
    lineItems: Array<{
      productId: number;
      variantId: number;
      quantity: number;
      properties: Record<string, string>;
    }>;
  }): Promise<any> {
    console.log('🔧 [DEV] Creating order via proxy:', orderData);

    try {
      const response = await fetch('/api/shopify/orders.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order: orderData }), // Shopify APIの形式に合わせる
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        order: data.order,
        message: '注文が作成されました'
      };
    } catch (error) {
      console.error('Failed to create order:', error);
      return {
        success: false,
        message: '注文の作成に失敗しました'
      };
    }
  }
}

export const devShopifyAPI = new DevShopifyAPI();
export type { MockProduct, MockCartItem };
